import imageCompression from 'browser-image-compression';

function checkAndConvertImage(fileName, altText = '', pathPrefix = '') { // 检查一个文件请求是否为图片

        // 常见图片扩展名列表
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.tiff'];
        
        // 获取文件扩展名（小写）
        const fileExt = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
        
        // 检查是否是图片
        const isImage = imageExtensions.includes(fileExt);
        
        // 准备结果字符串
        let result = fileName;
        
        if (isImage) {
            // 构建完整路径（添加前缀如果提供）
            const fullPath = pathPrefix ? `${pathPrefix}/${fileName}` : fileName;
            
            // 如果没有提供alt文本，使用文件名（不含扩展名）作为默认
            const alt = altText || fileName.slice(0, fileName.lastIndexOf('.'));
            
            // 生成Markdown图片语法
            result = `![${alt}](${fullPath})`;
        }
        
        return {
            isImage: isImage,
            result: result
        };
    }

function isFilePreviewable(fileName) { // 检查文件是否支持预览
        // 获取文件扩展名（转为小写，去掉点）
        const extension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
        
        // 支持预览的文件扩展名列表
        const previewableExtensions = [
            // 图片格式
            'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg',
            // 文本和文档
            'txt', 'md', 'markdown', 'csv',
            // 代码文件
            'js', 'jsx', 'ts', 'tsx', 'html', 'htm', 'css', 'scss', 'less',
            'json', 'xml', 'yml', 'yaml',
            'cpp', 'c', 'h', 'hpp', 'cc', 'java', 
            'py', 'rb', 'php', 'go', 'rs', 'swift',
            'sh', 'bash', 'ps1', 'bat',
            'sql', 'lua', 'kt', 'dart'
        ];
        
        return previewableExtensions.includes(extension);
    }
function getParentDirectory(path) {
  // 如果路径是根目录 '/'，直接返回 '/'
  if (path === '/' || path === '') {
    return '/';
  }

  // 去除末尾的斜杠（如果存在）
  const normalizedPath = path.replace(/\/+$/, '');

  // 找到最后一个 '/' 的位置
  const lastSlashIndex = normalizedPath.lastIndexOf('/');

  // 如果没有找到 '/'（例如 'abc'），返回根目录 '/'
  if (lastSlashIndex === 0) {
    return '/';
  }

  // 如果没有上级目录（即位于根下的一级目录），也返回根
  if (lastSlashIndex <= 0) {
    return '/';
  }

  // 返回从开头到上一个 '/' 的部分
  return normalizedPath.substring(0, lastSlashIndex) + '/';
}

/**
 * 使用第三方库压缩图片为WebP
 * @param {File} file - 原始图片文件
 * @param {Object} options - 压缩选项
 * @returns {Promise<File>} 压缩后的文件
 */
async function compressImageToWebP(file, options = {}) {
  const compressionOptions = {
    maxSizeMB: 10,              // 最大文件大小（MB）
    maxWidthOrHeight: 1920,    // 最大宽度或高度
    useWebWorker: true,        // 使用Web Worker提高性能
    fileType: 'image/webp',    // 输出格式
    quality: 0.8,              // 压缩质量
    ...options
  };

  try {
    const compressedFile = await imageCompression(file, compressionOptions);
    return compressedFile;
  } catch (error) {
    throw new Error('图片压缩失败: ' + error.message);
  }
}

/**
 * 检查是否为图片文件
 * @param {File} file - 文件对象
 * @returns {boolean} 是否为图片文件
 */
function isImageFile(file) {
  const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];
  return imageTypes.includes(file.type);
}

// 检查字符串是否包含控制字符的辅助函数
function hasControlCharacters(str) {
  for (let i = 0; i < str.length; i++) {
    const charCode = str.charCodeAt(i);
    if (charCode < 32 || charCode === 127) {
      return true;
    }
  }
  return false;
}

// 验证文件夹名称
function validateFolderName(name) {
  if (!name) {
    return '';
  }
  
  // 检查是否包含非法字符
  const invalidChars = /[<>:"/\\|?*]/;
  if (invalidChars.test(name)) {
    return '文件夹名称不能包含以下字符: < > : " / \\ | ? *';
  }
  
  // 检查是否包含控制字符
  if (hasControlCharacters(name)) {
    return '文件夹名称不能包含控制字符';
  }
  
  // 检查是否以点开头或结尾
  if (name.startsWith('.') || name.endsWith('.')) {
    return '文件夹名称不能以点(.)开头或结尾';
  }
  
  // 检查是否包含连续的点
  if (name.includes('..')) {
    return '文件夹名称不能包含连续的点(..)';
  }
  
  // 检查是否为空白字符
  if (name.trim() === '') {
    return '文件夹名称不能为空';
  }
  
  // 检查长度限制
  if (name.length > 255) {
    return '文件夹名称长度不能超过255个字符';
  }
  
  // 检查保留名称
  const reservedNames = ['CON', 'PRN', 'AUX', 'NUL', 
                        'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
                        'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'];
  if (reservedNames.includes(name.toUpperCase())) {
    return '文件夹名称不能使用系统保留名称';
  }
  
  return '';
}

// 验证文件名称
function validateFileName(name) {
  if (!name) {
    return '';
  }
  
  // 检查是否包含非法字符
  const invalidChars = /[<>:"/\\|?*]/;
  if (invalidChars.test(name)) {
    return '文件名称不能包含以下字符: < > : " / \\ | ? *';
  }
  
  // 检查是否包含控制字符
  if (hasControlCharacters(name)) {
    return '文件名称不能包含控制字符';
  }
  
  // 检查是否以点开头或结尾
  if (name.startsWith('.') || name.endsWith('.')) {
    return '文件名称不能以点(.)开头或结尾';
  }
  
  // 检查是否包含连续的点
  if (name.includes('..')) {
    return '文件名称不能包含连续的点(..)';
  }
  
  // 检查是否为空白字符
  if (name.trim() === '') {
    return '文件名称不能为空';
  }
  
  // 检查长度限制
  if (name.length > 255) {
    return '文件名称长度不能超过255个字符';
  }
  
  // 检查保留名称
  const reservedNames = ['CON', 'PRN', 'AUX', 'NUL', 
                        'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
                        'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'];
  if (reservedNames.includes(name.toUpperCase())) {
    return '文件名称不能使用系统保留名称';
  }
  
  // // 检查是否包含扩展名（文件应该有扩展名）
  // if (!name.includes('.')) {
  //   return '文件名称必须包含扩展名';
  // }
  
  // // 检查扩展名是否合法
  // const extension = name.split('.').pop();
  // if (!extension || extension.trim() === '') {
  //   return '文件扩展名不能为空';
  // }
  
  return '';
}

// 提取一个路径中的文件名或者文件夹名, 需要兼容各种情况
function getName(path) {
  if (typeof path !== 'string') {
    // 处理非字符串输入，根据需求决定返回值，这里返回 null
    return null;
  }

  // 移除路径末尾可能存在的一个或多个 "/"
  // 这样可以统一处理 "/test/" 和 "/test" 的情况
  const trimmedPath = path.replace(/\/+$/, '');

  // 如果移除末尾 "/" 后路径为空
  if (trimmedPath === '') {
    // 这意味着原始路径是由一个或多个 "/" 组成，例如 "/" 或 "///"
    // 通常这种情况下我们希望返回根目录的名称 "/"
    return '/';
  }

  // 使用 lastIndexOf 找到最后一个 "/" 的位置
  const lastSlashIndex = trimmedPath.lastIndexOf('/');

  // 提取最后一个 "/" 之后的部分
  // 如果路径中没有 "/" (例如 "filename.txt" 或 "foldername")，lastIndexOf 返回 -1
  // 在这种情况下，substring(lastSlashIndex + 1) 会返回整个字符串
  return trimmedPath.substring(lastSlashIndex + 1);

}

export{
    checkAndConvertImage,
    isFilePreviewable,
    getParentDirectory,
    compressImageToWebP,
    isImageFile,
    validateFileName,
    validateFolderName,
    hasControlCharacters,
    getName
}
